import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { ControlValueAccessor, FormBuilder, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, ValidatorFn, Validators } from '@angular/forms'
import { AUTHORIZATION_STATUS, AuthorizationStatusPullData, ClinicService, CustomFieldService, dateRangeValidator, Facility, getAuthorizationStatusesWithout, LimitationQuantityType, Limitations, PatientInsurance } from '@mmx/shared'
import moment from 'moment-timezone'
import { combineLatestWith, map, startWith, Subject, takeUntil, tap } from 'rxjs'

@Component({
  selector: 'mmx-auth-details-form',
  templateUrl: './auth-details-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: AuthDetailsFormComponent,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: AuthDetailsFormComponent,
      multi: true,
    },
  ],
})
export class AuthDetailsFormComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  @Input()
  public includeUnits = false

  @Input()
  public procedureCodes: string[]

  @Input()
  public diagnosisCodes: string[]

  @Input()
  public timezone: string

  @Input()
  public statusPullData: AuthorizationStatusPullData | undefined

  @Input()
  public outdated = false

  @Input()
  public ignoredOutdatedConflicts = false

  @Input()
  public outdatedWarnings: string[] = []

  @Input()
  public patientInsurances: PatientInsurance[]

  @Output()
  public ignoreOutdatedConflicts = new EventEmitter<void>()

  @Output()
  public pullStatusClicked = new EventEmitter<void>()

  public readonly AUTHORIZATION_STATUS = AUTHORIZATION_STATUS

  public readonly authorizationStatuses = getAuthorizationStatusesWithout([
    AUTHORIZATION_STATUS.NONE,
    AUTHORIZATION_STATUS.NOT_KNOWN,
    AUTHORIZATION_STATUS.NOT_FOUND,
  ])

  private readonly numberValidators = [
    Validators.minLength(6),
    Validators.maxLength(50),
  ]

  public formGroup = this.fb.group({
    id: this.fb.control<string>(null),
    number: this.fb.control<string>('', this.numberValidators),
    referenceNumber: this.fb.control<string>('', this.numberValidators),
    facility: this.fb.control<Facility>(null, [Validators.required]),
    facilityId: this.fb.control<string>(null),
    facilityNPI: this.fb.control<string>(null),
    procedureCodes: this.fb.control<string>(null),
    diagnosisCodes: this.fb.control<string>(null),
    insuranceId: this.fb.control<string>(null, [Validators.required]),
    carrierId: this.fb.control<string>(null),
    carrierLookupId: this.fb.control<string>(null),
    referrerId: this.fb.control<string>(null),
    providerIds: this.fb.control<string[]>(null),
    startAt: this.fb.control<moment.Moment>(null),
    endAt: this.fb.control<moment.Moment>(null),
    status: this.fb.control<AUTHORIZATION_STATUS>(AUTHORIZATION_STATUS.NONE, [Validators.required]),
    limitations: this.fb.group({
      quantity: this.fb.control<number>(null, [Validators.min(0)]),
      quantityType: this.fb.control<LimitationQuantityType>(null),
    }, { validators: this.unitsValidator() }),
    customFields: this.fb.control({}),
    note: this.fb.control<string>('', [Validators.maxLength(100)]),
  }, { validators: dateRangeValidator('startAt', 'endAt') } )

  public diff$ = this.formGroup.get('startAt').valueChanges.pipe(
    startWith(this.formGroup.value.startAt),
    combineLatestWith(this.formGroup.get('endAt').valueChanges.pipe(
      startWith(this.formGroup.value.endAt),
    )),
    map(([startAt, endAt]) => {
      if (moment.isMoment(startAt) && moment.isMoment(endAt)) {
        return endAt.diff(startAt, 'days')
      }
    }),
  )

  private onDestroy$ = new Subject<void>()
  private onChange: (value: any) => void = () => {}
  private onTouched: () => void = () => {}

  constructor(
    private readonly clinicService: ClinicService,
    public readonly customFieldsService: CustomFieldService,
    private readonly fb: FormBuilder,
    private cdr: ChangeDetectorRef,
  ) { }

  public ngOnInit() {
    this.formGroup.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        map(() => this.formatValue()),
      )
      .subscribe((value) => {
        this.onChange?.(value)
      })

    this.formGroup.get('status').valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        startWith(this.formGroup.value.status),
      )
      .subscribe((status) => {
        this.handleNumberValidation(status)
        this.handleDateTimeValidation(status)
        this.handleReferenceNumberValidation(status)
      })

    // ensure we update the carrierId when the selected patient insurance changes
    this.formGroup.get('insuranceId').valueChanges.pipe(
      takeUntil(this.onDestroy$),
      map((insuranceId) => this.patientInsurances.find(ins => ins.id === insuranceId)),
      tap((insurance) => {
        this.formGroup.patchValue({
          carrierLookupId: insurance?.carrierLookupId ?? null,
          carrierId: insurance?.carrierId ?? null,
        })
        this.formGroup.updateValueAndValidity()
      }),
    ).subscribe()
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public writeValue(value: any): void {
    this.formGroup.patchValue(value || {})
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  public validate(): ValidationErrors | null {
    return this.formGroup.valid ? null : { invalid: true }
  }

  public setTo(event: Event, controlName: string, offset: number, units: 'days' | 'months' | 'years' = 'days') {
    event.preventDefault()
    const control = this.formGroup.get(controlName)
    const now = controlName === 'endAt' && this.formGroup.value.startAt
      ? moment.tz(this.formGroup.value.startAt, this.timezone)
      : moment.tz(this.timezone)
    control.setValue(now.add(offset, units).startOf('day'))
    control.updateValueAndValidity()
  }

  private formatValue() {
    const value = this.formGroup.value

    return {
      status: value.status,
      number: value.number,
      referenceNumber: value.referenceNumber,
      insuranceId: value.insuranceId,
      carrierLookupId: value.carrierLookupId,
      carrierId: value.carrierId,
      facility: value.facility,
      facilityId: value.facility?.id,
      npi: value.facility?.provider?.id || this.clinicService.clinic?.provider?.id,
      referrerId: value.referrerId,
      providerIds: value.providerIds,
      procedureCodes: value.procedureCodes,
      diagnosisCodes: value.diagnosisCodes,
      startAt: value.startAt,
      endAt: value.endAt,
      customFields: value.customFields,
      note: value.note,
      priorAuthorization: undefined,
      limitations: this.includeUnits && value.limitations?.quantity != null ? {
        quantity: value.limitations.quantity,
        quantityType: value.limitations.quantityType,
      } : undefined,
    }
  }

  private handleNumberValidation(status: AUTHORIZATION_STATUS): void {
    const number = this.formGroup.get('number')
    if ([AUTHORIZATION_STATUS.APPROVED, AUTHORIZATION_STATUS.PARTIALLY_APPROVED].includes(status)) {
      number.setValidators([Validators.required, ...this.numberValidators])
    } else {
      number.setValidators(this.numberValidators)
    }
    number.updateValueAndValidity()
  }

  private handleDateTimeValidation(status: AUTHORIZATION_STATUS): void {
    const startAt = this.formGroup.get('startAt')
    const endAt = this.formGroup.get('endAt')
    if ([AUTHORIZATION_STATUS.APPROVED, AUTHORIZATION_STATUS.PARTIALLY_APPROVED].includes(status)) {
      startAt.setValidators([Validators.required])
      endAt.setValidators([Validators.required])
    } else {
      startAt.clearValidators()
      endAt.clearValidators()
    }
    startAt.updateValueAndValidity()
    endAt.updateValueAndValidity()
  }

  private handleReferenceNumberValidation(status: AUTHORIZATION_STATUS): void {
    const refNumber = this.formGroup.get('referenceNumber')
    if ([AUTHORIZATION_STATUS.INITIATED_CASE_INSURANCE, AUTHORIZATION_STATUS.INITIATED_CASE_MEDICAL].includes(status)) {
      refNumber.setValidators([Validators.required, ...this.numberValidators])
    } else {
      refNumber.setValidators(this.numberValidators)
    }
    refNumber.updateValueAndValidity()
  }

  private unitsValidator(): ValidatorFn {
    return (formGroup: FormGroup): ValidationErrors | null => {
      const limitations: Limitations = formGroup.value
      if (!limitations.quantity != !limitations.quantityType) {
        if (!limitations.quantity) {
          formGroup.controls.quantity.setErrors({ required: true })
        } else {
          formGroup.controls.quantityType.setErrors({ required: true })
        }
      }

      return null
    }
  }
}
